import { usePinnedTemplatePreference } from '../../lib/pinnedTemplatePreference';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import TemplatePreview from '../communications/TemplatePreview';
import { supabaseClient } from '../../config/supabase';
import { countSmsSegments } from '../../lib/communicationsCompose';
import { manageMessageTemplate, syncMessageTemplates } from '../../lib/messageTemplatesApi';
import {
    insertNextTemplateVariable,
    listVariablesInTemplate,
    MESSAGE_TEMPLATE_CATEGORIES,
    MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET,
    normalizeWhatsAppTemplateName,
    validateMessageTemplate,
} from '../../lib/messageTemplates';
import { TEMPLATE_FIELD_SOURCE_HELP, TEMPLATE_FIELD_SOURCE_OPTIONS } from '../../lib/templateFieldResolver';
import { getTemplateDisplayName } from '../../lib/communicationsTemplates';
import PhoneInput from '../ui/PhoneInput';
import ResponsiveTable, { ResponsiveCell } from '../shared/ResponsiveTable';
import AppLink from '../ui/AppLink';
import { clearTemplateSettingsReturn, readTemplateSettingsReturn, templateSettingsReturnHref } from '../../lib/templateSettingsReturn';
import './messageTemplates.css';

// Comms next pass (1.12.214-beta): one real tab per channel (no "All"). The tab is deep-linkable (?channel=sms) and
// restored from the URL; SMS is the default. RCS stays visible but disabled until carrier registration exists.
export const CHANNEL_TABS = [
    { value: 'sms', label: 'SMS' },
    { value: 'whatsapp', label: 'WhatsApp' },
    { value: 'email', label: 'Email' },
    { value: 'rcs', label: 'RCS', disabled: true, tooltip: 'Needs carrier registration' },
];
const ENABLED_CHANNEL_TABS = CHANNEL_TABS.filter((tab) => !tab.disabled).map((tab) => tab.value);
export const DEFAULT_TEMPLATE_CHANNEL = 'sms';

const CHANNEL_FOOTNOTES = {
    sms: 'SMS templates go live when saved — no approval step.',
    whatsapp: 'WhatsApp templates need Meta\u2019s approval (we submit for you; status updates here).',
    email: 'Email templates go live when saved — no approval step.',
};

/** The channel tab named by ?channel= (mms → SMS), or SMS when missing / unknown / disabled. */
export function readChannelTabFromUrl(search = typeof window === 'undefined' ? '' : window.location.search) {
    const raw = String(new URLSearchParams(search).get('channel') || '').toLowerCase();
    const value = raw === 'mms' ? 'sms' : raw;
    return ENABLED_CHANNEL_TABS.includes(value) ? value : DEFAULT_TEMPLATE_CHANNEL;
}

function updateChannelParamInUrl(channel) {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    params.set('channel', channel);
    const query = params.toString();
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`);
}

/**
 * WAI-ARIA tabs keyboard model (automatic activation): Left/Right move and wrap, Home/End jump, disabled tabs are skipped.
 * Returns the value to activate, or null when the key is not a tab-navigation key.
 */
export function nextTabValue(values, current, key, isDisabled = () => false) {
    const enabled = values.filter((value) => !isDisabled(value));
    if (enabled.length === 0) return null;
    const index = Math.max(0, enabled.indexOf(current));
    if (key === 'ArrowRight') return enabled[(index + 1) % enabled.length];
    if (key === 'ArrowLeft') return enabled[(index - 1 + enabled.length) % enabled.length];
    if (key === 'Home') return enabled[0];
    if (key === 'End') return enabled[enabled.length - 1];
    return null;
}

const TEMPLATE_SCOPES = [
    ['active', 'Active'],
    ['archived', 'Archived'],
];

const HEADER_OPTIONS = [
    ['none', 'None'],
    ['text', 'Text'],
    ['image', 'Image'],
    ['video', 'Video'],
    ['document', 'Document'],
];

const BUTTON_TYPES = [
    ['quick_reply', 'Quick reply'],
    ['phone', 'Call phone number'],
    ['url', 'Visit website'],
    ['copy_code', 'Copy code'],
];

const STATUS_META = {
    draft: { label: 'Draft', className: 'border-gray-500/60 bg-transparent text-gray-400' },
    pending: { label: 'Pending Meta review', className: 'border-amber-400/70 bg-transparent text-amber-300' },
    approved: { label: 'Approved', className: 'border-[#22C55E]/70 bg-transparent text-[#22C55E]' },
    rejected: { label: 'Rejected', className: 'border-red-400/70 bg-transparent text-red-300' },
    paused: { label: 'Paused', className: 'border-orange-400/70 bg-transparent text-orange-300' },
    disabled: { label: 'Disabled', className: 'border-gray-500/60 bg-transparent text-gray-400' },
};
const STATUS_CHECK_COOLDOWN_MS = 60 * 1000;

const HEADER_MEDIA_RULES = {
    image: {
        accept: 'image/jpeg,image/png',
        size: 5 * 1024 * 1024,
        types: ['image/jpeg', 'image/png'],
        help: 'PNG or JPG, up to 5 MB.',
    },
    video: {
        accept: 'video/mp4,video/3gpp',
        size: 16 * 1024 * 1024,
        types: ['video/mp4', 'video/3gpp'],
        help: 'MP4 or 3GPP, up to 16 MB.',
    },
    document: {
        accept: 'application/pdf',
        size: 100 * 1024 * 1024,
        types: ['application/pdf'],
        help: 'PDF, up to 100 MB.',
    },
};

const VARIABLE_PATTERN = /\{\{\s*([^{}]+?)\s*\}\}/g;

function emptyTemplate(channel = 'whatsapp') {
    return {
        id: null,
        channel,
        name: '',
        display_name: '',
        category: 'utility',
        language: 'en',
        header: { type: 'none' },
        body: '',
        footer: '',
        buttons: [],
        variables: {},
        emailSubject: '',
        status: channel === 'whatsapp' ? 'draft' : 'approved',
        rejection_reason: null,
        archived_at: null,
        updated_at: null,
        updated_by: null,
        last_synced_at: null,
    };
}

function normalizeTemplateRow(row = {}) {
    return {
        ...emptyTemplate(row.channel || 'whatsapp'),
        ...row,
        display_name: row?.display_name ?? '',
        header: row?.header && typeof row.header === 'object' && !Array.isArray(row.header) ? row.header : { type: 'none' },
        buttons: Array.isArray(row?.buttons) ? row.buttons : [],
        variables: row?.variables && typeof row.variables === 'object' && !Array.isArray(row.variables) ? row.variables : {},
        emailSubject: row?.email_subject ?? row?.emailSubject ?? '',
    };
}

function formatChannel(channel = '') {
    const normalized = String(channel || '').toLowerCase();
    if (normalized === 'sms') return 'SMS';
    if (normalized === 'whatsapp') return 'WhatsApp';
    if (normalized === 'email') return 'Email';
    if (normalized === 'rcs') return 'RCS';
    return channel || '—';
}

function formatCategory(category = '') {
    if (!category) return '—';
    return category.charAt(0).toUpperCase() + category.slice(1);
}

function formatTime(value) {
    if (!value) return '—';
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime())) return '—';
    return parsed.toLocaleString();
}

function readTemplateIdFromUrl() {
    if (typeof window === 'undefined') return null;
    const templateId = new URLSearchParams(window.location.search).get('template');
    return templateId || null;
}

function updateTemplateParamInUrl(templateId) {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    if (templateId) params.set('template', templateId);
    else params.delete('template');
    const query = params.toString();
    const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', nextUrl);
}

function eventActionLabel(event = {}) {
    const action = String(event.action || '').toLowerCase();
    if (action === 'status_changed') {
        return `Status changed${event.from_status ? ` (${event.from_status} → ${event.to_status || '—'})` : ` (${event.to_status || '—'})`}`;
    }
    if (action === 'submitted') return 'Submitted to Meta';
    if (action === 'created') return 'Created';
    if (action === 'updated') return 'Updated';
    if (action === 'archived') return 'Archived';
    if (action === 'restored') return 'Restored';
    if (action === 'deleted') return 'Deleted';
    if (action === 'synced') return 'Synced';
    return action || 'Updated';
}

function extractVariableKeysForDraft(draft = {}) {
    if (draft.channel === 'whatsapp') {
        return listVariablesInTemplate({
            body: draft.body,
            header: draft.header,
            footer: draft.footer,
            buttons: draft.buttons,
        });
    }
    const keys = new Set();
    [`${draft.emailSubject || ''}`, `${draft.body || ''}`].forEach((source) => {
        for (const match of source.matchAll(VARIABLE_PATTERN)) keys.add(match[1]);
    });
    return [...keys];
}

function syncDraftVariables(draft = {}) {
    const keys = extractVariableKeysForDraft(draft);
    const variables = keys.reduce((acc, key) => {
        acc[key] = {
            sample: draft?.variables?.[key]?.sample || '',
            source: draft?.variables?.[key]?.source || 'manual',
        };
        return acc;
    }, {});
    return { ...draft, variables };
}

function serializeDraft(draft = {}) {
    return {
        channel: draft.channel,
        name: draft.name,
        display_name: draft.display_name || '',
        category: draft.channel === 'whatsapp' ? draft.category : null,
        language: draft.language || 'en',
        header: draft.header,
        body: draft.body,
        footer: draft.footer,
        buttons: draft.buttons,
        variables: draft.variables,
        email_subject: draft.emailSubject || '',
        status: draft.status,
    };
}

function isWhatsappReadOnly(template = null) {
    return template?.channel === 'whatsapp' && template?.status !== 'draft';
}

function canEditTemplate(template = null) {
    if (!template) return false;
    if (template.channel === 'whatsapp') return template.status === 'draft';
    return template.channel === 'sms' || template.channel === 'email';
}

function lockedTemplateMessage(template = null) {
    if (!isWhatsappReadOnly(template)) return '';
    if (template?.status === 'approved') {
        return 'This template is approved by WhatsApp/carriers and can\'t be changed. Make an editable copy to create a new version.';
    }
    if (template?.status === 'pending') {
        return 'This template is under review and can\'t be changed right now. Make an editable copy to create a new version.';
    }
    if (template?.status === 'rejected') {
        return 'This template was rejected and can\'t be changed in place. Fix and resubmit to carry the Meta rejection reason forward, or make an editable copy to create a new version.';
    }
    const label = STATUS_META[template?.status]?.label?.toLowerCase() || 'locked';
    return `This template is ${label} in WhatsApp and can't be changed in place. Make an editable copy to create a new version.`;
}

function deriveLastSyncedAt(templates = []) {
    return templates.reduce((latest, template) => {
        if (!template?.last_synced_at) return latest;
        if (!latest) return template.last_synced_at;
        return new Date(template.last_synced_at).getTime() > new Date(latest).getTime()
            ? template.last_synced_at
            : latest;
    }, null);
}

function MessageTemplatesSettingsView({
    theme = 'light',
    orgId = null,
    client = supabaseClient,
    api = {},
    initialChannel = null,
    embedded = false,
}) {
    const dark = theme === 'dark';
    const messageTemplateApi = useMemo(() => ({
        manageMessageTemplate,
        syncMessageTemplates,
        ...(api || {}),
    }), [api]);
    const [templates, setTemplates] = useState([]);
    const [profilesById, setProfilesById] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [error, setError] = useState('');
    const [flash, setFlash] = useState('');
    const [activeChannel, setActiveChannelState] = useState(() => initialChannel || readChannelTabFromUrl());
    useEffect(() => { if (initialChannel) setActiveChannelState(initialChannel); }, [initialChannel]);
    const channelTabRefs = useRef({});
    // Comms next pass: arrived from a reply box's Templates → "Edit templates": offer the way back (the draft waits there).
    const [returnNote] = useState(() => readTemplateSettingsReturn());
    // Comms final pass (#759 follow-up): the note is for ONE trip back. Leaving Templates any other way (another menu, another
    // page) means it no longer applies, so it is cleared then, instead of lingering for up to 6 hours. "Back to conversation"
    // keeps it for Communications, which reads it on arrival and clears it itself.
    const returningRef = useRef(false);
    useEffect(() => () => {
        if (returnNote && !returningRef.current) clearTemplateSettingsReturn();
    }, [returnNote]);
    const scopeTabRefs = useRef({});
    const setActiveChannel = useCallback((channel) => {
        setActiveChannelState(channel);
        if (!embedded) updateChannelParamInUrl(channel);
    }, [embedded]);
    const [activeScope, setActiveScope] = useState('active');
    const [canManage, setCanManage] = useState(false);
    const [currentUserId, setCurrentUserId] = useState(null);
    const pins = usePinnedTemplatePreference(currentUserId, client);
    const channelPinCount = templates.filter(t => t.channel === activeChannel && pins.ids.includes(t.id)).length;
    const togglePin = async template => {
        const pinned = pins.ids.includes(template.id);
        if (!pinned && channelPinCount >= 5) { setError('You can pin up to 5 templates per channel. Unpin one first.'); return; }
        try { await pins.save(pinned ? pins.ids.filter(id => id !== template.id) : [...pins.ids, template.id], {channel:activeChannel,templates}); setError(''); }
        catch(failure) { setError(failure.message || 'Could not save pins.'); }
    };
    const [orgName, setOrgName] = useState('Your business');
    const [lastSyncedAt, setLastSyncedAt] = useState(null);
    const [editorTemplateId, setEditorTemplateId] = useState(null);
    const [draft, setDraft] = useState(null);
    const [templateEventsById, setTemplateEventsById] = useState({});
    const [templateEventActorsById, setTemplateEventActorsById] = useState({});
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyError, setHistoryError] = useState('');
    const [uploadingHeader, setUploadingHeader] = useState(false);
    const [editorError, setEditorError] = useState('');
    const [resubmitPinnedReason, setResubmitPinnedReason] = useState(null);
    const [statusCheckByTemplate, setStatusCheckByTemplate] = useState({});
    const [cooldownNow, setCooldownNow] = useState(() => Date.now());
    const [deepLinkHandled, setDeepLinkHandled] = useState(false);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const bodyRef = useRef(null);
    const deleteDialogRef = useRef(null);
    const deleteDialogPrimaryActionRef = useRef(null);
    const deleteReturnFocusRef = useRef(null);

    const loadTemplates = useCallback(async () => {
        if (!orgId) {
            setTemplates([]);
            setProfilesById({});
            setLastSyncedAt(null);
            setLoading(false);
            return;
        }
        setLoading(true);
        setError('');
        const { data, error: loadError } = await client
            .from('message_templates')
            .select('id, org_id, channel, name, display_name, category, language, header, body, footer, buttons, variables, email_subject, status, rejection_reason, twilio_content_sid, version, parent_template_id, archived_at, archived_by, created_by, updated_by, created_at, updated_at, last_synced_at')
            .eq('org_id', orgId)
            .order('updated_at', { ascending: false });
        if (loadError) {
            setTemplates([]);
            setError(loadError.message || 'Could not load message templates.');
            setLoading(false);
            return;
        }
        const rows = (data || []).map(normalizeTemplateRow);
        setTemplates(rows);
        setLastSyncedAt(deriveLastSyncedAt(rows));
        const profileIds = [...new Set(rows.map((row) => row.updated_by).filter(Boolean))];
        if (profileIds.length > 0) {
            const { data: profiles } = await client.from('user_profiles').select('id, full_name').in('id', profileIds);
            setProfilesById(Object.fromEntries((profiles || []).map((profile) => [profile.id, profile.full_name || 'Staff'])));
        } else {
            setProfilesById({});
        }
        setLoading(false);
    }, [client, orgId]);

    useEffect(() => {
        let mounted = true;
        async function loadViewer() {
            const { data: authData } = await client.auth.getUser();
            const userId = authData?.user?.id || null;
            if (!mounted) return;
            setCurrentUserId(userId);
            setOrgName(
                authData?.user?.user_metadata?.org_name
                || globalThis.localStorage?.getItem('intelAgentTenantName')
                || 'Your business'
            );
            if (!orgId || !userId) {
                setCanManage(false);
                return;
            }
            const { data: membership } = await client
                .from('org_members')
                .select('role')
                .eq('org_id', orgId)
                .eq('user_id', userId)
                .maybeSingle();
            if (mounted) {
                setCanManage(['owner', 'admin'].includes(membership?.role || ''));
            }
        }
        loadViewer().catch(() => {
            if (mounted) setCanManage(false);
        });
        return () => { mounted = false; };
    }, [client, orgId]);

    useEffect(() => {
        loadTemplates().catch((loadError) => {
            setError(loadError.message || 'Could not load message templates.');
            setLoading(false);
        });
    }, [loadTemplates]);

    useEffect(() => {
        setDeepLinkHandled(false);
    }, [orgId]);

    const loadTemplateHistory = useCallback(async (templateId) => {
        if (!orgId || !templateId) return;
        setHistoryLoading(true);
        setHistoryError('');
        try {
            const { data: events, error: eventsError } = await client
                .from('message_template_events')
                .select('id, template_id, org_id, actor_id, action, from_status, to_status, detail, created_at')
                .eq('org_id', orgId)
                .eq('template_id', templateId)
                .order('created_at', { ascending: false });
            if (eventsError) throw eventsError;
            const rows = Array.isArray(events) ? events : [];
            setTemplateEventsById((current) => ({ ...current, [templateId]: rows }));

            const actorIds = [...new Set(rows.map((row) => row.actor_id).filter(Boolean))];
            if (actorIds.length > 0) {
                const { data: actors } = await client.from('user_profiles').select('id, full_name').in('id', actorIds);
                setTemplateEventActorsById((current) => ({
                    ...current,
                    [templateId]: Object.fromEntries((actors || []).map((actor) => [actor.id, actor.full_name || 'Staff'])),
                }));
            } else {
                setTemplateEventActorsById((current) => ({ ...current, [templateId]: {} }));
            }
        } catch (loadHistoryError) {
            setHistoryError(loadHistoryError.message || 'Could not load template history.');
        } finally {
            setHistoryLoading(false);
        }
    }, [client, orgId]);

    useEffect(() => {
        const templateId = editorTemplateId || draft?.id;
        if (!templateId || !historyOpen) return;
        loadTemplateHistory(templateId).catch((loadHistoryError) => {
            setHistoryError(loadHistoryError.message || 'Could not load template history.');
            setHistoryLoading(false);
        });
    }, [draft?.id, editorTemplateId, historyOpen, loadTemplateHistory]);

    useEffect(() => {
        if (loading || deepLinkHandled || templates.length === 0) return;
        const templateIdFromUrl = readTemplateIdFromUrl();
        if (!templateIdFromUrl) {
            setDeepLinkHandled(true);
            return;
        }
        const match = templates.find((template) => template.id === templateIdFromUrl);
        if (match) {
            if (ENABLED_CHANNEL_TABS.includes(match.channel)) setActiveChannel(match.channel);
            setEditorTemplateId(match.id);
            setDraft(syncDraftVariables(normalizeTemplateRow(match)));
            setResubmitPinnedReason(null);
            setHistoryOpen(false);
            setHistoryError('');
        }
        setDeepLinkHandled(true);
    }, [deepLinkHandled, loading, setActiveChannel, templates]);

    const visibleTemplates = useMemo(() => templates.filter((template) => {
        const archived = Boolean(template.archived_at);
        if (activeScope === 'active' && archived) return false;
        if (activeScope === 'archived' && !archived) return false;
        return template.channel === activeChannel;
    }).sort((a,b) => Number(pins.ids.includes(b.id)) - Number(pins.ids.includes(a.id))), [activeChannel, activeScope, templates, pins.ids]);
    const isWhatsAppTab = activeChannel === 'whatsapp';
    const activeChannelLabel = CHANNEL_TABS.find((tab) => tab.value === activeChannel)?.label || 'SMS';

    const validation = useMemo(() => {
        if (!draft) return { valid: true, errors: [], normalized: {} };
        return validateMessageTemplate(serializeDraft(draft));
    }, [draft]);

    const variableKeys = useMemo(() => (draft ? extractVariableKeysForDraft(draft) : []), [draft]);
    const smsMetrics = useMemo(() => countSmsSegments(draft?.channel === 'sms' ? draft.body : ''), [draft]);
    const editorTemplate = useMemo(
        () => templates.find((template) => template.id === editorTemplateId) || null,
        [editorTemplateId, templates]
    );
    const activeEditorTemplate = editorTemplate || draft;
    const activeEditorEvents = activeEditorTemplate?.id ? (templateEventsById[activeEditorTemplate.id] || []) : [];
    const activeEditorEventActors = activeEditorTemplate?.id ? (templateEventActorsById[activeEditorTemplate.id] || {}) : {};
    const parentTemplate = useMemo(() => {
        const parentId = activeEditorTemplate?.parent_template_id;
        if (!parentId) return null;
        return templates.find((template) => template.id === parentId) || null;
    }, [activeEditorTemplate?.parent_template_id, templates]);
    const readOnly = !canManage
        || Boolean(activeEditorTemplate?.archived_at)
        || (Boolean(activeEditorTemplate?.id) && !canEditTemplate(activeEditorTemplate))
        || (!activeEditorTemplate?.id && isWhatsappReadOnly(activeEditorTemplate));
    const versionLabel = activeEditorTemplate?.version && Number(activeEditorTemplate.version) > 1
        ? `Version ${activeEditorTemplate.version}`
        : 'Version 1';
    const canFixAndResubmit = Boolean(
        readOnly
        && canManage
        && activeEditorTemplate?.channel === 'whatsapp'
        && activeEditorTemplate?.status === 'rejected'
    );
    const canArchiveOldVersion = Boolean(
        canManage
        && activeEditorTemplate?.status === 'approved'
        && parentTemplate?.status === 'rejected'
        && !parentTemplate?.archived_at
    );
    const lockedWhatsAppMessage = lockedTemplateMessage(activeEditorTemplate);

    const updateDraft = (patch) => {
        setDraft((current) => syncDraftVariables({
            ...(current || emptyTemplate()),
            ...patch,
            header: patch?.header ? patch.header : (current?.header || { type: 'none' }),
            buttons: patch?.buttons ? patch.buttons : (current?.buttons || []),
            variables: patch?.variables ? patch.variables : (current?.variables || {}),
        }));
    };

    const openEditor = (template = null, { pinnedReason = null } = {}) => {
        // A new template starts on the channel of the tab it was created from.
        const nextDraft = syncDraftVariables(template ? normalizeTemplateRow(template) : emptyTemplate(activeChannel));
        setEditorTemplateId(template?.id || null);
        setDraft(nextDraft);
        setResubmitPinnedReason(pinnedReason);
        setHistoryOpen(false);
        setHistoryError('');
        updateTemplateParamInUrl(template?.id || null);
        setEditorError('');
        setFlash('');
    };

    const closeEditor = () => {
        setEditorTemplateId(null);
        setDraft(null);
        setHistoryOpen(false);
        setHistoryError('');
        setResubmitPinnedReason(null);
        updateTemplateParamInUrl(null);
        setEditorError('');
    };

    const closeDeleteDialog = useCallback(() => {
        const focusTarget = deleteReturnFocusRef.current;
        setDeleteTarget(null);
        requestAnimationFrame(() => {
            if (focusTarget && document.contains(focusTarget)) focusTarget.focus();
            deleteReturnFocusRef.current = null;
        });
    }, []);

    const handleTemplateAction = async (payload, successMessage, { reload = true } = {}) => {
        setSaving(true);
        setEditorError('');
        setError('');
        try {
            const response = await messageTemplateApi.manageMessageTemplate({ orgId, ...payload });
            if (reload) await loadTemplates();
            if (successMessage) setFlash(successMessage);
            return response;
        } catch (actionError) {
            setEditorError(actionError.message || 'Could not save the template.');
            return null;
        } finally {
            setSaving(false);
        }
    };

    const saveDraft = async ({ submitAfterSave = false } = {}) => {
        if (!draft) return;
        const action = draft.id ? 'update' : 'create';
        const saved = await handleTemplateAction({
            action,
            templateId: draft.id || undefined,
            template: serializeDraft(draft),
        }, submitAfterSave ? '' : 'Template saved.');
        if (!saved?.template) return;
        const nextTemplate = normalizeTemplateRow(saved.template);
        if (submitAfterSave && nextTemplate.channel === 'whatsapp') {
            const submitted = await handleTemplateAction({
                action: 'submit',
                templateId: nextTemplate.id,
            }, 'Template submitted to Meta.');
            if (submitted?.template || submitted?.twilioContentSid) {
                closeEditor();
            }
            return;
        }
        setEditorTemplateId(nextTemplate.id);
        setDraft(syncDraftVariables(nextTemplate));
        if (nextTemplate.channel !== 'whatsapp') closeEditor();
    };

    const duplicateTemplate = async (templateId, { openAfter = false } = {}) => {
        const sourceTemplate = templates.find((template) => template.id === templateId) || null;
        const result = await handleTemplateAction({
            action: 'duplicate',
            templateId,
        }, 'Template duplicated.');
        const duplicated = normalizeTemplateRow(result?.template || {});
        if (openAfter && duplicated?.id) {
            setEditorTemplateId(duplicated.id);
            setDraft(syncDraftVariables(duplicated));
            setHistoryOpen(false);
            setHistoryError('');
            setResubmitPinnedReason(
                sourceTemplate?.status === 'rejected' && sourceTemplate?.rejection_reason
                    ? sourceTemplate.rejection_reason
                    : null
            );
            updateTemplateParamInUrl(duplicated.id);
        }
    };

    const fixAndResubmitTemplate = async (template) => {
        if (!template?.id) return;
        await duplicateTemplate(template.id, { openAfter: true });
    };

    const archiveTemplate = async (template, restore = false) => {
        await handleTemplateAction({
            action: restore ? 'restore' : 'archive',
            templateId: template.id,
        }, restore ? 'Template restored.' : 'Template archived.');
        if (editorTemplateId === template.id) closeEditor();
    };

    const archivePreviousRejectedVersion = async () => {
        if (!parentTemplate?.id) return;
        await archiveTemplate(parentTemplate, false);
    };

    const confirmDeleteTemplate = async () => {
        if (!deleteTarget) return;
        const template = deleteTarget;
        await handleTemplateAction({
            action: 'delete',
            templateId: template.id,
            confirm: template.channel === 'whatsapp' ? true : undefined,
        }, 'Template deleted.');
        if (editorTemplateId === template.id) closeEditor();
        closeDeleteDialog();
    };

    const deleteTemplate = (template, trigger = null) => {
        deleteReturnFocusRef.current = trigger;
        setDeleteTarget(template);
    };

    const archiveFromDeleteDialog = async () => {
        if (!deleteTarget) return;
        const template = deleteTarget;
        await archiveTemplate(template, Boolean(template.archived_at));
        closeDeleteDialog();
    };

    const triggerSync = async () => {
        setSyncing(true);
        setError('');
        try {
            const result = await messageTemplateApi.syncMessageTemplates({ orgId });
            setLastSyncedAt(result?.syncedAt || new Date().toISOString());
            setFlash('Twilio sync finished.');
            await loadTemplates();
            return true;
        } catch (syncError) {
            setError(syncError.message || 'Could not sync templates with Twilio.');
            return false;
        } finally {
            setSyncing(false);
        }
    };

    const checkStatusRemainingSeconds = (templateId) => {
        const lastTriggeredAt = statusCheckByTemplate[templateId] || 0;
        const remainingMs = STATUS_CHECK_COOLDOWN_MS - (cooldownNow - lastTriggeredAt);
        return remainingMs > 0 ? Math.ceil(remainingMs / 1000) : 0;
    };

    const triggerTemplateStatusCheck = async (template) => {
        if (!template?.id) return;
        if (checkStatusRemainingSeconds(template.id) > 0) return;
        const syncOk = await triggerSync();
        if (syncOk) {
            setStatusCheckByTemplate((current) => ({ ...current, [template.id]: Date.now() }));
        }
    };

    useEffect(() => {
        const hasActiveCooldown = Object.values(statusCheckByTemplate)
            .some((timestamp) => (cooldownNow - timestamp) < STATUS_CHECK_COOLDOWN_MS);
        if (!hasActiveCooldown) return undefined;
        const intervalId = window.setInterval(() => {
            setCooldownNow(Date.now());
        }, 1000);
        return () => window.clearInterval(intervalId);
    }, [cooldownNow, statusCheckByTemplate]);

    const handleChannelChange = (channel) => {
        const next = emptyTemplate(channel);
        if (channel === 'sms' || channel === 'email') next.status = 'approved';
        setDraft(syncDraftVariables(next));
    };

    const handleNameChange = (value) => {
        if (draft?.channel === 'whatsapp') {
            updateDraft({ name: normalizeWhatsAppTemplateName(value) });
            return;
        }
        updateDraft({ name: value });
    };

    const handleBodyChange = (value) => updateDraft({ body: value });

    const handleAddVariable = () => {
        if (!draft || !bodyRef.current) return;
        const inserted = insertNextTemplateVariable(draft.body, {
            start: bodyRef.current.selectionStart,
            end: bodyRef.current.selectionEnd,
        });
        updateDraft({ body: inserted.value });
        requestAnimationFrame(() => {
            bodyRef.current?.focus();
            bodyRef.current?.setSelectionRange(inserted.nextSelectionStart, inserted.nextSelectionEnd);
        });
    };

    const handleVariableSampleChange = (key, sample) => {
        updateDraft({
            variables: {
                ...(draft?.variables || {}),
                [key]: {
                    sample,
                    source: draft?.variables?.[key]?.source || 'manual',
                },
            },
        });
    };

    const handleVariableSourceChange = (key, source) => {
        updateDraft({
            variables: {
                ...(draft?.variables || {}),
                [key]: {
                    sample: draft?.variables?.[key]?.sample || '',
                    source: source || 'manual',
                },
            },
        });
    };

    const updateButton = (index, patch) => {
        const buttons = [...(draft?.buttons || [])];
        buttons[index] = { ...(buttons[index] || {}), ...patch };
        updateDraft({ buttons });
    };

    const updateButtonType = (index, type) => {
        const buttons = [...(draft?.buttons || [])];
        buttons[index] = { ...(buttons[index] || {}), type };
        updateDraft({ buttons });
    };

    const buttonDetailValue = (button = {}) => {
        if (button.type === 'phone') return button.phone || '';
        if (button.type === 'copy_code') return button.code || '';
        if (button.type === 'url') return button.url || '';
        return '';
    };

    const removeButton = (index) => {
        updateDraft({ buttons: (draft?.buttons || []).filter((_, currentIndex) => currentIndex !== index) });
    };

    const addButton = () => {
        updateDraft({
            buttons: [...(draft?.buttons || []), { type: 'quick_reply', text: '' }],
        });
    };

    const handleHeaderTypeChange = (type) => {
        if (type === 'text') {
            updateDraft({ header: { type: 'text', text: draft?.header?.text || '' } });
            return;
        }
        if (type === 'none') {
            updateDraft({ header: { type: 'none' } });
            return;
        }
        updateDraft({ header: { type, storage_path: draft?.header?.storage_path || '' } });
    };

    const uploadHeaderMedia = async (event) => {
        const file = event.target.files?.[0];
        if (!file || !draft || !orgId) return;
        const rules = HEADER_MEDIA_RULES[draft.header?.type];
        if (!rules) return;
        if (file.size > rules.size) {
            setEditorError(`Header ${draft.header.type} file is too large. ${rules.help}`);
            return;
        }
        if (rules.types.length > 0 && !rules.types.includes(file.type)) {
            setEditorError(`Header ${draft.header.type} file type is not allowed. ${rules.help}`);
            return;
        }
        setUploadingHeader(true);
        setEditorError('');
        const extension = (file.name.split('.').pop() || 'bin').toLowerCase();
        const uniqueId = globalThis.crypto?.randomUUID?.() || `${Date.now()}`;
        const objectPath = `${orgId}/${uniqueId}.${extension}`;
        const { data, error: uploadError } = await client.storage
            .from(MESSAGE_TEMPLATE_HEADER_MEDIA_BUCKET)
            .upload(objectPath, file, { upsert: true, contentType: file.type || undefined });
        setUploadingHeader(false);
        if (uploadError || !data?.path) {
            setEditorError(uploadError?.message || 'Could not upload header media.');
            return;
        }
        updateDraft({
            header: {
                type: draft.header.type,
                storage_path: data.path,
                file_name: file.name,
            },
        });
    };

    useEffect(() => {
        if (!deleteTarget) return undefined;
        requestAnimationFrame(() => {
            deleteDialogPrimaryActionRef.current?.focus();
        });
        const handleKeyDown = (event) => {
            if (!deleteDialogRef.current) return;
            if (event.key === 'Escape') {
                event.preventDefault();
                closeDeleteDialog();
                return;
            }
            if (event.key !== 'Tab') return;
            const focusable = Array.from(
                deleteDialogRef.current.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')
            ).filter((element) => !element.disabled);
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            const activeInsideDialog = deleteDialogRef.current.contains(document.activeElement);
            if (!activeInsideDialog) {
                event.preventDefault();
                (event.shiftKey ? last : first).focus();
            } else if (event.shiftKey && document.activeElement === first) {
                event.preventDefault();
                last.focus();
            } else if (!event.shiftKey && document.activeElement === last) {
                event.preventDefault();
                first.focus();
            }
        };
        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [closeDeleteDialog, deleteTarget]);

    return (
        <div data-message-template-settings="true" data-theme={theme} className={`ia-message-templates ${embedded ? 'ia-template-panel-list' : ''} p-6 ${dark ? 'text-gray-100' : 'text-gray-800'}`}>
            {returnNote && (
                <AppLink
                    to={templateSettingsReturnHref(returnNote)}
                    onClick={() => { returningRef.current = true; }}
                    data-template-back-to-conversation="true"
                    className={`mb-4 inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#03B7C9] ${dark
                        ? 'border-[#67E8F9]/60 text-[#67E8F9] hover:bg-[#67E8F9]/10'
                        : 'border-[#02808D]/60 text-[#02707B] hover:bg-[#02808D]/10'}`}
                >
                    <span aria-hidden="true">←</span>
                    {returnNote.kind === 'compose' ? 'Back to your new message' : 'Back to conversation'}
                    {returnNote.body.trim() && <span className="font-normal opacity-90">(your draft is kept)</span>}
                </AppLink>
            )}
            <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
                <div>
                    <h2 className="text-2xl font-semibold">Message templates</h2>
                    <p className={`mt-1 text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                        Build SMS, WhatsApp, and Email templates for outbound communications.
                    </p>
                    <p className={`mt-2 text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
                        Last synced: {lastSyncedAt ? formatTime(lastSyncedAt) : 'Not yet synced'}
                    </p>
                </div>
                {canManage && (
                    <div className="flex flex-wrap gap-2">
                        <button
                            type="button"
                            onClick={() => openEditor(null)}
                            className="rounded border border-[#22C55E] px-4 py-2 text-sm font-medium text-[#15803D] hover:bg-[#22C55E]/10"
                        >
                            New template
                        </button>
                        <button
                            type="button"
                            onClick={triggerSync}
                            disabled={syncing}
                            className={`rounded border px-4 py-2 text-sm font-medium ${dark ? 'border-gray-600 text-gray-100' : 'border-gray-300 text-gray-700'} disabled:opacity-60`}
                        >
                            {syncing ? 'Syncing…' : 'Sync with Twilio'}
                        </button>
                    </div>
                )}
            </div>

            {error && (
                <div role="alert" className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-red-500/60 bg-red-950/40 text-red-200' : 'border-red-300 bg-red-50 text-red-700'}`}>
                    {error}
                </div>
            )}
            {flash && (
                <div className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-[#22C55E]/60 bg-[#052e16] text-[#bbf7d0]' : 'border-green-300 bg-green-50 text-green-700'}`}>
                    {flash}
                </div>
            )}

            <div
                className="ia-template-channel-tabs mt-5 flex flex-wrap gap-1 border-b"
                role="tablist"
                aria-label="Template channel"
            >
                {CHANNEL_TABS.map((tab) => {
                    const selected = activeChannel === tab.value;
                    return (
                        <button
                            key={tab.value}
                            ref={(node) => { channelTabRefs.current[tab.value] = node; }}
                            id={`message-templates-tab-${tab.value}`}
                            type="button"
                            role="tab"
                            aria-selected={selected}
                            aria-controls={selected ? 'message-templates-panel' : undefined}
                            aria-disabled={tab.disabled ? 'true' : undefined}
                            tabIndex={selected ? 0 : -1}
                            title={tab.tooltip || undefined}
                            data-template-channel-tab={tab.value}
                            onClick={() => { if (!tab.disabled) setActiveChannel(tab.value); }}
                            onKeyDown={(event) => {
                                const next = nextTabValue(
                                    CHANNEL_TABS.map((item) => item.value),
                                    activeChannel,
                                    event.key,
                                    (value) => Boolean(CHANNEL_TABS.find((item) => item.value === value)?.disabled),
                                );
                                if (!next) return;
                                event.preventDefault();
                                setActiveChannel(next);
                                channelTabRefs.current[next]?.focus();
                            }}
                            className={`ia-template-channel-tab -mb-px rounded-t-lg border border-b-2 px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#03B7C9] ${
                                selected
                                    ? `ia-template-channel-tab--on border-x-transparent border-t-transparent ${dark
                                        ? 'border-b-[#67E8F9] text-[#67E8F9]'
                                        : 'border-b-[#02808D] text-[#02707B]'}`
                                    : dark
                                        ? 'border-transparent text-gray-300'
                                        : 'border-transparent text-gray-600'
                            } ${tab.disabled ? 'cursor-not-allowed opacity-70' : 'hover:border-b-[var(--selected-pill-border)]'}`}
                        >
                            {tab.label}
                            {tab.disabled && <span className="sr-only"> (needs carrier registration)</span>}
                        </button>
                    );
                })}
            </div>

            <div
                id="message-templates-panel"
                role="tabpanel"
                aria-labelledby={`message-templates-tab-${activeChannel}`}
                data-template-channel-panel={activeChannel}
            >
                <div className="mt-4 flex gap-2" role="tablist" aria-label={`${activeChannelLabel} template scope`}>
                    {TEMPLATE_SCOPES.map(([value, label]) => (
                        <button
                            key={value}
                            ref={(node) => { scopeTabRefs.current[value] = node; }}
                            type="button"
                            role="tab"
                            aria-selected={activeScope === value}
                            tabIndex={activeScope === value ? 0 : -1}
                            onClick={() => setActiveScope(value)}
                            onKeyDown={(event) => {
                                const next = nextTabValue(TEMPLATE_SCOPES.map(([scope]) => scope), activeScope, event.key);
                                if (!next) return;
                                event.preventDefault();
                                setActiveScope(next);
                                scopeTabRefs.current[next]?.focus();
                            }}
                            className={`ia-hover-pulse rounded-t-lg border px-3 py-1.5 text-sm ${
                                activeScope === value
                                    ? 'ia-selected-pill bg-transparent'
                                    : dark
                                        ? 'border-gray-600 text-gray-400'
                                        : 'border-gray-300 text-gray-500'
                            }`}
                        >
                            {label}
                        </button>
                    ))}
                </div>

                <p className="mt-2 text-sm" role="status" data-template-pin-count="true">{channelPinCount} of 5 pinned</p>
                <div className={`mt-2 overflow-hidden rounded-xl border ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                    {loading ? (
                        <div className="p-6 text-sm text-gray-500">Loading templates…</div>
                    ) : visibleTemplates.length === 0 ? (
                        <div className="p-6 text-sm text-gray-500">
                            {activeScope === 'archived'
                                ? `No archived ${activeChannelLabel} templates.`
                                : `No ${activeChannelLabel} templates yet.${canManage ? ' Use “New template” to create one.' : ''}`}
                        </div>
                    ) : (
                        <ResponsiveTable className="min-w-full divide-y divide-gray-200 text-sm">
                            <thead className={dark ? 'bg-gray-800 text-gray-300' : 'bg-gray-50 text-gray-600'}>
                                <tr>
                                    <th className="px-4 py-3 text-left font-medium">Name</th>
                                    {isWhatsAppTab && <th className="px-4 py-3 text-left font-medium">Category</th>}
                                    {isWhatsAppTab && <th className="px-4 py-3 text-left font-medium">Meta approval</th>}
                                    <th className="px-4 py-3 text-left font-medium">Edited by</th>
                                    <th className="px-4 py-3 text-left font-medium">Updated</th>
                                    {canManage && <th className="px-4 py-3 text-right font-medium">Actions</th>}
                                </tr>
                            </thead>
                            <tbody className={dark ? 'divide-y divide-gray-700 bg-gray-900/50' : 'divide-y divide-gray-100 bg-white'}>
                                {visibleTemplates.map((template) => {
                                    const status = STATUS_META[template.status] || STATUS_META.draft;
                                    const editedBy = profilesById[template.updated_by] || (template.updated_by ? 'Staff' : 'System');
                                    const checkStatusCooldown = checkStatusRemainingSeconds(template.id);
                                    return (
                                        <tr key={template.id}>
                                            <ResponsiveCell label="Name" className="px-4 py-3 align-top">
                                                {/* Comms polish hotfix: the pin sits on the name's own row, to its right, about one text line tall. */}
                                                <div className="ia-template-name-row" data-template-name-row="true">
                                                    <button
                                                        type="button"
                                                        onClick={() => openEditor(template)}
                                                        className="ia-template-name text-left font-medium text-[#02808D] underline"
                                                    >
                                                        <span title={getTemplateDisplayName(template)}>{getTemplateDisplayName(template)}</span>
                                                    </button>
                                                    <button type="button" aria-label={`${pins.ids.includes(template.id) ? 'Unpin' : 'Pin'} ${getTemplateDisplayName(template)}`} aria-pressed={pins.ids.includes(template.id)} disabled={!currentUserId || pins.saving} onClick={() => togglePin(template)} data-template-pin="true" className={`ia-template-pin rounded-full border ${pins.ids.includes(template.id) ? 'ia-selected-pill' : ''}`}><span aria-hidden="true">📌</span></button>
                                                </div>
                                                {template.display_name && <p className="mt-1 font-mono text-[10px] text-gray-500">{template.name}</p>}
                                                {isWhatsAppTab && template.status === 'rejected' && template.rejection_reason && (
                                                    <p className="mt-1 text-xs text-red-400">{template.rejection_reason}</p>
                                                )}
                                            </ResponsiveCell>
                                            {isWhatsAppTab && <ResponsiveCell label="Category" className="px-4 py-3 align-top">{formatCategory(template.category)}</ResponsiveCell>}
                                            {isWhatsAppTab && (
                                                <ResponsiveCell label="Meta approval" className="px-4 py-3 align-top">
                                                    <span
                                                        data-template-status={template.status} className={`ia-template-status inline-flex rounded-full border px-2 py-0.5 text-xs font-medium ${status.className}`}
                                                    >
                                                        {status.label}
                                                    </span>
                                                </ResponsiveCell>
                                            )}
                                            <ResponsiveCell label="Edited by" className="px-4 py-3 align-top">{editedBy}</ResponsiveCell>
                                            <ResponsiveCell label="Updated" className="px-4 py-3 align-top">{formatTime(template.updated_at)}</ResponsiveCell>
                                            {canManage && (
                                                <ResponsiveCell label="Actions" className="px-4 py-3 align-top text-right">
                                                    <div className="flex flex-wrap justify-end gap-2">
                                                        {canEditTemplate(template) && (
                                                            <button type="button" onClick={() => openEditor(template)} className="text-xs text-[#02808D] underline">Edit</button>
                                                        )}
                                                        {template.channel === 'whatsapp' && template.status === 'rejected' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => duplicateTemplate(template.id, { openAfter: true })}
                                                                className="text-xs font-semibold text-[#15803D] underline"
                                                            >
                                                                Fix and resubmit
                                                            </button>
                                                        )}
                                                        {template.channel === 'whatsapp' && template.status === 'pending' && (
                                                            <button
                                                                type="button"
                                                                onClick={() => triggerTemplateStatusCheck(template)}
                                                                disabled={syncing || checkStatusCooldown > 0}
                                                                className="text-xs text-[#02808D] underline disabled:opacity-60"
                                                            >
                                                                {checkStatusCooldown > 0 ? `Check status in ${checkStatusCooldown}s` : 'Check status now'}
                                                            </button>
                                                        )}
                                                        <button type="button" onClick={() => duplicateTemplate(template.id)} className="text-xs text-[#02808D] underline">Duplicate</button>
                                                        <button
                                                            type="button"
                                                            onClick={() => archiveTemplate(template, Boolean(template.archived_at))}
                                                            className="text-xs text-[#02808D] underline"
                                                        >
                                                            {template.archived_at ? 'Restore' : 'Archive'}
                                                        </button>
                                                        <button type="button" onClick={(event) => deleteTemplate(template, event.currentTarget)} className="text-xs text-red-600 underline">Delete</button>
                                                    </div>
                                                </ResponsiveCell>
                                            )}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </ResponsiveTable>
                    )}
                </div>


                <p className={`mt-3 text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`} data-template-channel-footnote="true">
                    {CHANNEL_FOOTNOTES[activeChannel]}
                </p>
            </div>

            {draft && (
                <div className={`mt-6 rounded-2xl border p-5 ${dark ? 'border-gray-700 bg-gray-900/70' : 'border-gray-200 bg-white'}`}>
                    <div className="flex items-start justify-between gap-4">
                        <div>
                            <h3 className="text-xl font-semibold">
                                {draft.id ? (readOnly ? 'View template' : 'Edit template') : 'New template'}
                            </h3>
                            {activeEditorTemplate?.id && (
                                <p className={`mt-1 text-xs ${dark ? 'text-gray-500' : 'text-gray-500'}`}>
                                    {parentTemplate?.name ? (
                                        <>
                                            Copy of{' '}
                                            <button
                                                type="button"
                                                onClick={() => openEditor(parentTemplate)}
                                                className="underline text-[#02808D]"
                                            >
                                                {parentTemplate.name}
                                            </button>
                                            {' · '}
                                            {versionLabel}
                                        </>
                                    ) : versionLabel}
                                </p>
                            )}
                            <p className={`mt-1 text-sm ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                {!canManage
                                    ? 'You can review template details here, but only org owners/admins can make changes.'
                                    : activeEditorTemplate?.archived_at
                                    ? 'Archived templates are read-only until they are restored.'
                                    : lockedWhatsAppMessage
                                    ? 'This WhatsApp template is locked. Use the action below to create a new editable version.'
                                    : 'Save SMS and Email templates directly. Submit WhatsApp drafts to Meta when ready.'}
                            </p>
                        </div>
                        <button type="button" onClick={closeEditor} className="text-sm underline">Close</button>
                    </div>

                    {lockedWhatsAppMessage && canManage && (
                        <div className={`mt-4 flex flex-col gap-3 rounded border px-4 py-3 text-sm md:flex-row md:items-center md:justify-between ${dark ? 'border-amber-500/60 bg-amber-950/40 text-amber-100' : 'border-amber-300 bg-amber-50 text-amber-900'}`}>
                            <p>{lockedWhatsAppMessage}</p>
                            <div className="flex flex-wrap gap-3">
                                {canFixAndResubmit && (
                                    <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => fixAndResubmitTemplate(activeEditorTemplate)}
                                        className="rounded border border-[#22C55E] px-4 py-2 text-sm font-semibold text-[#15803D] disabled:opacity-60"
                                    >
                                        Fix and resubmit
                                    </button>
                                )}
                                <button
                                    type="button"
                                    disabled={saving}
                                    onClick={() => duplicateTemplate(draft.id, { openAfter: true })}
                                    className={`rounded border px-4 py-2 text-sm ${canFixAndResubmit ? 'border-gray-300 font-medium text-gray-700' : 'border-[#22C55E] font-semibold text-[#15803D]'} disabled:opacity-60`}
                                >
                                    Make an editable copy
                                </button>
                            </div>
                        </div>
                    )}

                    {resubmitPinnedReason && (
                        <div role="alert" className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-red-500/60 bg-red-950/40 text-red-200' : 'border-red-300 bg-red-50 text-red-700'}`}>
                            <span className="font-semibold">Meta rejection reason: </span>
                            {resubmitPinnedReason}
                        </div>
                    )}
                    {canArchiveOldVersion && (
                        <div className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-amber-500/60 bg-amber-950/40 text-amber-200' : 'border-amber-300 bg-amber-50 text-amber-700'}`}>
                            Previous rejected version: <span className="font-semibold" title={parentTemplate?.name}>{getTemplateDisplayName(parentTemplate)}</span>
                            <button
                                type="button"
                                onClick={archivePreviousRejectedVersion}
                                className="ml-2 underline text-[#02808D]"
                            >
                                Archive old version
                            </button>
                        </div>
                    )}
                    {editorError && (
                        <div className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-orange-500/60 bg-orange-950/40 text-orange-200' : 'border-orange-300 bg-orange-50 text-orange-700'}`}>
                            {editorError}
                        </div>
                    )}
                    {!validation.valid && !readOnly && (
                        <div className={`mt-4 rounded border px-4 py-3 text-sm ${dark ? 'border-orange-500/60 bg-orange-950/40 text-orange-200' : 'border-orange-300 bg-orange-50 text-orange-700'}`}>
                            <ul className="list-disc pl-5">
                                {validation.errors.map((entry) => <li key={entry}>{entry}</li>)}
                            </ul>
                        </div>
                    )}

                    <div className="ia-template-editor-layout mt-5 grid gap-6">
                        <div className="space-y-5">
                            <section>
                                <p className="mb-2 text-sm font-medium">Channel</p>
                                <div className="flex flex-wrap gap-2">
                                    {['whatsapp', 'sms', 'email'].map((channel) => (
                                        <button
                                            key={channel}
                                            type="button"
                                            disabled={Boolean(draft.id)}
                                            onClick={() => handleChannelChange(channel)}
                                            className={`ia-hover-pulse rounded-full border bg-transparent px-3 py-1 text-sm ${
                                                draft.channel === channel
                                                    ? 'ia-selected-pill'
                                                    : dark
                                                        ? 'border-gray-600 text-gray-300'
                                                        : 'border-gray-300 text-gray-600'
                                            } ${draft.id ? 'cursor-not-allowed opacity-70' : ''}`}
                                        >
                                            {formatChannel(channel)}
                                        </button>
                                    ))}
                                </div>
                            </section>

                            <Field
                                label="Name"
                                hint={draft.channel === 'whatsapp' ? 'Lowercase letters, digits, and underscores only.' : null}
                            >
                                <input
                                    aria-label="Template name"
                                    value={draft.name}
                                    onChange={(event) => handleNameChange(event.target.value)}
                                    disabled={readOnly}
                                    className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                />
                            </Field>

                            <Field label="Display name" hint="Optional staff-facing name. This does not change the WhatsApp or Twilio template name.">
                                <input
                                    aria-label="Template display name"
                                    value={draft.display_name || ''}
                                    maxLength={80}
                                    onChange={(event) => updateDraft({ display_name: event.target.value })}
                                    disabled={readOnly}
                                    className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                />
                            </Field>

                            {draft.channel === 'whatsapp' && (
                                <>
                                    <div className="grid gap-4 md:grid-cols-2">
                                        <Field label="Category">
                                            <select
                                                aria-label="Template category"
                                                value={draft.category}
                                                disabled={readOnly}
                                                onChange={(event) => updateDraft({ category: event.target.value })}
                                                className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                            >
                                                {MESSAGE_TEMPLATE_CATEGORIES.map((category) => (
                                                    <option key={category} value={category}>{formatCategory(category)}</option>
                                                ))}
                                            </select>
                                        </Field>
                                        <Field label="Language">
                                            <input
                                                aria-label="Template language"
                                                value={draft.language}
                                                disabled={readOnly}
                                                onChange={(event) => updateDraft({ language: event.target.value })}
                                                className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                            />
                                        </Field>
                                    </div>

                                    <Field label="Header">
                                        <div className="space-y-3">
                                            <div className="flex flex-wrap gap-2">
                                                {HEADER_OPTIONS.map(([value, label]) => (
                                                    <button
                                                        key={value}
                                                        type="button"
                                                        disabled={readOnly}
                                                        onClick={() => handleHeaderTypeChange(value)}
                                                        className={`ia-hover-pulse rounded-full border bg-transparent px-3 py-1 text-sm ${
                                                            draft.header?.type === value
                                                                ? 'ia-selected-pill'
                                                                : dark
                                                                    ? 'border-gray-600 text-gray-300'
                                                                    : 'border-gray-300 text-gray-600'
                                                        } ${readOnly ? 'opacity-70' : ''}`}
                                                    >
                                                        {label}
                                                    </button>
                                                ))}
                                            </div>
                                            {draft.header?.type === 'text' && (
                                                <>
                                                    <textarea
                                                        aria-label="WhatsApp header text"
                                                        value={draft.header?.text || ''}
                                                        disabled={readOnly}
                                                        onChange={(event) => updateDraft({ header: { type: 'text', text: event.target.value } })}
                                                        className={`min-h-[72px] w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                                    />
                                                    <Counter value={(draft.header?.text || '').length} limit={60} />
                                                </>
                                            )}
                                            {['image', 'video', 'document'].includes(draft.header?.type) && (
                                                <div className="space-y-2">
                                                    <p className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                                        {HEADER_MEDIA_RULES[draft.header.type]?.help}
                                                    </p>
                                                    {!readOnly && (
                                                        <input
                                                            aria-label="Header media upload"
                                                            type="file"
                                                            accept={HEADER_MEDIA_RULES[draft.header.type]?.accept}
                                                            onChange={uploadHeaderMedia}
                                                            disabled={uploadingHeader}
                                                        />
                                                    )}
                                                    <p className="text-xs text-gray-500">
                                                        {uploadingHeader
                                                            ? 'Uploading header media…'
                                                            : (draft.header?.file_name || draft.header?.storage_path || 'No file uploaded yet.')}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </Field>
                                </>
                            )}

                            {draft.channel === 'email' && (
                                <Field label="Subject">
                                    <input
                                        aria-label="Email subject"
                                        value={draft.emailSubject}
                                        disabled={readOnly}
                                        onChange={(event) => updateDraft({ emailSubject: event.target.value })}
                                        className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                    />
                                </Field>
                            )}

                            <Field label="Body">
                                <div className="space-y-2">
                                    {['whatsapp', 'sms'].includes(draft.channel) && !readOnly && (
                                        <button type="button" onClick={handleAddVariable} className="text-sm font-medium text-[#02808D] underline">
                                            Add variable
                                        </button>
                                    )}
                                    <textarea
                                        ref={bodyRef}
                                        aria-label="Template body"
                                        value={draft.body}
                                        disabled={readOnly}
                                        onChange={(event) => handleBodyChange(event.target.value)}
                                        className={`min-h-[160px] w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                    />
                                    {draft.channel === 'whatsapp' && <Counter value={draft.body.length} limit={1024} />}
                                    {draft.channel === 'sms' && (
                                        <p className={`text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                            {smsMetrics.characters} characters · {smsMetrics.segments} {smsMetrics.segments === 1 ? 'text' : 'texts'} · {smsMetrics.encoding}
                                        </p>
                                    )}
                                </div>
                            </Field>

                            {variableKeys.length > 0 && (
                                <Field label="Sample values">
                                    <div className="grid gap-3 md:grid-cols-2">
                                        {variableKeys.map((key) => (
                                            <label key={key} className="block text-sm">
                                                <span className="mb-1 block font-medium">{`{{${key}}}`}</span>
                                                <input
                                                    aria-label={`Sample value ${key}`}
                                                    value={draft.variables?.[key]?.sample || ''}
                                                    disabled={readOnly}
                                                    onChange={(event) => handleVariableSampleChange(key, event.target.value)}
                                                    className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                                />
                                                <span className={`mb-1 mt-2 block text-xs font-medium ${dark ? 'text-gray-300' : 'text-gray-600'}`}>Fill from</span>
                                                <select
                                                    aria-label={`Fill from ${key}`}
                                                    aria-describedby={`fill-from-help-${key}`}
                                                    value={draft.variables?.[key]?.source || 'manual'}
                                                    disabled={readOnly}
                                                    onChange={(event) => handleVariableSourceChange(key, event.target.value)}
                                                    className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                                >
                                                    {TEMPLATE_FIELD_SOURCE_OPTIONS.map((option) => (
                                                        <option key={option.value} value={option.value}>
                                                            {option.label}
                                                        </option>
                                                    ))}
                                                </select>
                                                <span id={`fill-from-help-${key}`} className={`ia-fill-from-help mt-1 block text-xs ${dark ? 'text-gray-300' : 'text-gray-600'}`} data-fill-from-help="true">
                                                    {TEMPLATE_FIELD_SOURCE_HELP}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </Field>
                            )}

                            {draft.channel === 'whatsapp' && (
                                <>
                                    <Field label="Footer">
                                        <div className="space-y-2">
                                            <textarea
                                                aria-label="WhatsApp footer"
                                                value={draft.footer}
                                                disabled={readOnly}
                                                onChange={(event) => updateDraft({ footer: event.target.value })}
                                                className={`min-h-[72px] w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'} disabled:opacity-70`}
                                            />
                                            <Counter value={draft.footer.length} limit={60} />
                                        </div>
                                    </Field>

                                    <Field label="Buttons">
                                        <div className="space-y-3">
                                            {(draft.buttons || []).map((button, index) => (
                                                <div key={`${button.type || 'button'}-${index}`} className={`rounded-xl border p-3 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                                                    <div className="flex items-center justify-between gap-2">
                                                        <select
                                                            aria-label={`Button type ${index + 1}`}
                                                            value={button.type || 'quick_reply'}
                                                            disabled={readOnly}
                                                            onChange={(event) => updateButtonType(index, event.target.value)}
                                                            className={`rounded border px-2 py-1 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                                                        >
                                                            {BUTTON_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
                                                        </select>
                                                        {!readOnly && (
                                                            <button type="button" onClick={() => removeButton(index)} className="text-xs text-red-600 underline">
                                                                Remove
                                                            </button>
                                                        )}
                                                    </div>
                                                    <div className="mt-3 grid gap-3 md:grid-cols-2">
                                                        <label className="block text-sm">
                                                            <span className="mb-1 block font-medium">Button text</span>
                                                            <input
                                                                aria-label={`Button text ${index + 1}`}
                                                                value={button.text || ''}
                                                                disabled={readOnly}
                                                                onChange={(event) => updateButton(index, { text: event.target.value })}
                                                                className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                                                            />
                                                            <Counter value={(button.text || '').length} limit={25} />
                                                        </label>
                                                        {(button.type === 'phone' || button.type === 'url' || button.type === 'copy_code') && (
                                                            <label className="block text-sm">
                                                                <span className="mb-1 block font-medium">
                                                                    {button.type === 'phone' ? 'Phone number' : button.type === 'copy_code' ? 'Code' : 'Website URL'}
                                                                </span>
                                                                {button.type === 'phone' ? (
                                                                    <PhoneInput
                                                                        aria-label={`Button detail ${index + 1}`}
                                                                        value={buttonDetailValue(button)}
                                                                        disabled={readOnly}
                                                                        onChange={(phone) => updateButton(index, { phone })}
                                                                        className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                                                                    />
                                                                ) : (
                                                                    <input
                                                                        aria-label={`Button detail ${index + 1}`}
                                                                        value={buttonDetailValue(button)}
                                                                        disabled={readOnly}
                                                                        onChange={(event) => updateButton(index, button.type === 'copy_code'
                                                                            ? { code: event.target.value }
                                                                            : { url: event.target.value })}
                                                                        className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                                                                    />
                                                                )}
                                                            </label>
                                                        )}
                                                    </div>
                                                    {button.type === 'url' && (
                                                        <label className="mt-3 block text-sm">
                                                            <span className="mb-1 block font-medium">Dynamic URL suffix (optional)</span>
                                                            <input
                                                                aria-label={`Button URL suffix ${index + 1}`}
                                                                value={button.dynamicSuffix || ''}
                                                                disabled={readOnly}
                                                                onChange={(event) => updateButton(index, { dynamicSuffix: event.target.value })}
                                                                className={`w-full rounded-lg border px-3 py-2 text-sm ${dark ? 'border-gray-600 bg-gray-800 text-gray-100' : 'border-gray-300 bg-white text-gray-800'}`}
                                                            />
                                                        </label>
                                                    )}
                                                </div>
                                            ))}
                                            {!readOnly && (
                                                <button type="button" onClick={addButton} className="text-sm font-medium text-[#02808D] underline">
                                                    Add button
                                                </button>
                                            )}
                                        </div>
                                    </Field>
                                </>
                            )}

                            <div className="flex flex-wrap gap-3">
                                {!readOnly && draft.channel === 'whatsapp' && (
                                    <>
                                        <button
                                            type="button"
                                            disabled={saving || !validation.valid}
                                            onClick={() => saveDraft({ submitAfterSave: false })}
                                            className="rounded border border-gray-300 px-4 py-2 text-sm font-medium disabled:opacity-60"
                                        >
                                            {saving ? 'Saving…' : 'Save draft'}
                                        </button>
                                        <button
                                            type="button"
                                            disabled={saving || !validation.valid}
                                            onClick={() => saveDraft({ submitAfterSave: true })}
                                            className="rounded border border-[#22C55E] px-4 py-2 text-sm font-medium text-[#15803D] disabled:opacity-60"
                                        >
                                            Save &amp; submit to Meta
                                        </button>
                                    </>
                                )}
                                {!readOnly && (draft.channel === 'sms' || draft.channel === 'email') && (
                                    <button
                                        type="button"
                                        disabled={saving || !validation.valid}
                                        onClick={() => saveDraft({ submitAfterSave: false })}
                                        className="rounded border border-[#22C55E] px-4 py-2 text-sm font-medium text-[#15803D] disabled:opacity-60"
                                    >
                                        {saving ? 'Saving…' : 'Save'}
                                    </button>
                                )}
                                {readOnly && canManage && !lockedWhatsAppMessage && (
                                    <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => duplicateTemplate(draft.id, { openAfter: true })}
                                        className="rounded border border-[#22C55E] px-4 py-2 text-sm font-medium text-[#15803D] disabled:opacity-60"
                                    >
                                        Make an editable copy
                                    </button>
                                )}
                            </div>

                            {activeEditorTemplate?.id && (
                                <section className={`mt-4 rounded-xl border ${dark ? 'border-gray-700 bg-gray-900/40' : 'border-gray-200 bg-gray-50'}`}>
                                    <button
                                        type="button"
                                        onClick={() => setHistoryOpen((current) => !current)}
                                        className="flex w-full items-center justify-between px-4 py-3 text-left text-sm font-semibold"
                                    >
                                        <span>History</span>
                                        <span>{historyOpen ? 'Hide' : 'Show'}</span>
                                    </button>
                                    {historyOpen && (
                                        <div className="border-t border-gray-200 px-4 py-3 text-sm">
                                            {historyLoading ? (
                                                <p className="text-xs text-gray-500">Loading history…</p>
                                            ) : historyError ? (
                                                <p className="text-xs text-red-500">{historyError}</p>
                                            ) : activeEditorEvents.length === 0 ? (
                                                <p className="text-xs text-gray-500">No history yet.</p>
                                            ) : (
                                                <ul className="space-y-2">
                                                    {activeEditorEvents.map((eventRow) => (
                                                        <li key={eventRow.id} className={`rounded border px-3 py-2 ${dark ? 'border-gray-700' : 'border-gray-200'}`}>
                                                            <p className="font-medium">{eventActionLabel(eventRow)}</p>
                                                            <p className="text-xs text-gray-500">
                                                                {(activeEditorEventActors[eventRow.actor_id] || (eventRow.actor_id ? 'Staff' : 'System'))}
                                                                {' · '}
                                                                {formatTime(eventRow.created_at)}
                                                            </p>
                                                            {(eventRow.to_status === 'rejected' || eventRow.action === 'status_changed') && eventRow.detail?.rejectionReason && (
                                                                <p className="mt-1 text-xs text-red-500">Meta reason: {eventRow.detail.rejectionReason}</p>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    )}
                                </section>
                            )}
                        </div>

                        <aside className={`rounded-2xl border p-4 ${dark ? 'border-gray-700 bg-gray-800/60' : 'border-gray-200 bg-gray-50'}`}>
                            <p className="text-sm font-semibold">Preview</p>
                            <p className={`mt-1 text-xs ${dark ? 'text-gray-400' : 'text-gray-500'}`}>
                                Live preview with sample values and channel-specific rendering.
                            </p>
                            <div className="mt-4">
                                <TemplatePreview template={draft} theme={theme} orgName={orgName} client={client} />
                            </div>
                        </aside>
                    </div>
                </div>
            )}
            {deleteTarget && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
                    <div
                        ref={deleteDialogRef}
                        role="dialog"
                        aria-modal="true"
                        aria-label="Delete template confirmation"
                        className={`w-full max-w-md rounded-2xl border p-5 shadow-2xl ${dark ? 'border-gray-700 bg-gray-900 text-gray-100' : 'border-gray-200 bg-white text-gray-800'}`}
                    >
                        <h3 className="text-lg font-semibold">Delete template?</h3>
                        <p className={`mt-2 text-sm ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                            {deleteTarget.channel === 'whatsapp'
                                ? 'Meta may block reusing the same deleted name for a while. Archive is recommended unless you are sure.'
                                : 'Deleting a template cannot be undone.'}
                        </p>
                        {deleteTarget.channel === 'whatsapp' && (
                            <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${dark ? 'border-orange-500/60 bg-orange-950/40 text-orange-200' : 'border-orange-300 bg-orange-50 text-orange-700'}`}>
                                Archive keeps the template history in IntelAgent and is the recommended action before hard delete.
                            </div>
                        )}
                        <div className="mt-5 flex flex-wrap justify-end gap-2">
                            <button
                                type="button"
                                onClick={closeDeleteDialog}
                                className={`rounded border px-4 py-2 text-sm font-medium ${dark ? 'border-gray-600 text-gray-200' : 'border-gray-300 text-gray-700'}`}
                            >
                                Cancel
                            </button>
                            {deleteTarget.channel === 'whatsapp' && (
                                <button
                                    ref={deleteDialogPrimaryActionRef}
                                    type="button"
                                    onClick={archiveFromDeleteDialog}
                                    className="rounded border border-[#22C55E] px-4 py-2 text-sm font-medium text-[#22C55E]"
                                >
                                    Archive instead
                                </button>
                            )}
                            <button
                                ref={deleteTarget.channel === 'whatsapp' ? null : deleteDialogPrimaryActionRef}
                                type="button"
                                onClick={confirmDeleteTemplate}
                                className={`rounded border px-4 py-2 text-sm font-medium ${dark ? 'border-red-500/60 text-red-300' : 'border-red-300 text-red-700'}`}
                            >
                                Delete
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function Field({ label, hint = null, children }) {
    return (
        <label className="block">
            <span className="mb-1 block text-sm font-medium">{label}</span>
            {hint && <span className="mb-2 block text-xs text-gray-500">{hint}</span>}
            {children}
        </label>
    );
}

function Counter({ value = 0, limit = 0 }) {
    return (
        <p className={`text-xs ${value > limit ? 'text-red-600' : 'text-gray-500'}`}>
            {value}/{limit}
        </p>
    );
}

export default MessageTemplatesSettingsView;
